import { useEffect, useId, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { useSetModelPrice } from '../hooks/useSetModelPrice';
import type { ModelPriceView } from '../types';

/**
 * Recording a rate for one model, whether it currently has none (spec 077 US2) or
 * already has one being corrected/renewed from a later date (spec 095 follow-up).
 *
 * Inline on the panel that named the model, not behind a dialog. The whole complaint this
 * feature answers is that an administrator could see a gap and not act on it; putting the
 * remedy one click further away than the diagnosis would keep half of that.
 *
 * The model key is fixed, never typed. It comes from the list, so it cannot be
 * mistyped — and a typo here would create a rate for a model that does not exist while
 * leaving the real one unpriced, which reads as "I fixed it" and is invisible afterwards.
 */
export function RecordPriceForm({
  modelKey,
  currentPrice,
  onDone,
}: {
  modelKey: string;
  /**
   * The model's rate on file right now, if any (spec 095 follow-up) — pre-fills the
   * rate fields so "Change rate" starts from what is actually in effect rather than a
   * blank sheet the admin has to retype from memory. `undefined` for a model with no
   * rate yet, where a blank sheet is correct.
   */
  currentPrice?: ModelPriceView;
  onDone: () => void;
}) {
  const mutation = useSetModelPrice();
  const ids = {
    from: useId(),
    input: useId(),
    output: useId(),
    cacheRead: useId(),
    cacheCreation: useId(),
  };

  const [effectiveFrom, setEffectiveFrom] = useState(() =>
    defaultEffectiveFrom(currentPrice),
  );
  // Set once the admin edits the date, so a price arriving late moves only a default,
  // never a date someone chose.
  const dateTouched = useRef(false);
  const [rates, setRates] = useState({
    input_rate: currentPrice?.input_rate ?? '',
    output_rate: currentPrice?.output_rate ?? '',
    cache_read_rate: currentPrice?.cache_read_rate ?? '',
    cache_creation_rate: currentPrice?.cache_creation_rate ?? '',
  });

  // `currentPrice` can arrive after this form has already mounted: it comes from
  // `useModelPrices`, a query separate from the profile list this row itself renders
  // from, so a row can open (and a form mount) before that price has resolved. The
  // `useState` initializer above only ever reads it once, at that first render — so
  // without this, a price that resolves a moment later would never reach the fields,
  // and "Change rate" would open blank instead of pre-filled. Fires at most once, the
  // first time a price shows up, so it never clobbers what an admin has since typed.
  const hasPrefilled = useRef(currentPrice !== undefined);
  useEffect(() => {
    if (hasPrefilled.current || !currentPrice) return;
    hasPrefilled.current = true;
    if (!dateTouched.current) {
      setEffectiveFrom(defaultEffectiveFrom(currentPrice));
    }
    setRates({
      input_rate: currentPrice.input_rate,
      output_rate: currentPrice.output_rate,
      cache_read_rate: currentPrice.cache_read_rate,
      cache_creation_rate: currentPrice.cache_creation_rate,
    });
  }, [currentPrice]);

  const complete =
    effectiveFrom !== '' &&
    Object.values(rates).every((value) => value.trim() !== '');

  return (
    <form
      className="border-border mt-3 flex flex-col gap-3 rounded-lg border p-3"
      onSubmit={(event) => {
        event.preventDefault();
        mutation.mutate(
          {
            model_key: modelKey,
            // Midnight UTC on the chosen day. The period boundaries this is compared
            // against are UTC, so reading the input as local time would put a rate on the
            // wrong side of a month end for anyone west of Greenwich.
            effective_from: `${effectiveFrom}T00:00:00Z`,
            ...rates,
          },
          { onSuccess: onDone },
        );
      }}
    >
      <div className="flex w-1/2 flex-col gap-1.5">
        <Label htmlFor={ids.from}>Effective from</Label>
        <Input
          id={ids.from}
          type="date"
          value={effectiveFrom}
          onChange={(event) => {
            dateTouched.current = true;
            setEffectiveFrom(event.target.value);
          }}
          disabled={mutation.isPending}
        />
        {currentPrice ? (
          <p className="text-muted-foreground text-xs">
            The current rate applies from{' '}
            {shortUtcDate(currentPrice.effective_from)}.
          </p>
        ) : null}
        <p className="text-muted-foreground text-xs">
          Runs before this date keep whatever rate applied to them. A rate that
          began today can be corrected; an earlier one is never edited — add a
          new one from a later date instead.
        </p>
      </div>

      <fieldset className="grid grid-cols-2 gap-3">
        <legend className="text-muted-foreground mb-1 text-xs">
          Rates in USD per million tokens, as the vendor publishes them.
        </legend>
        {(
          [
            [ids.input, 'input_rate', 'Input'],
            [ids.output, 'output_rate', 'Output'],
            [ids.cacheRead, 'cache_read_rate', 'Cache read'],
            [ids.cacheCreation, 'cache_creation_rate', 'Cache write'],
          ] as const
        ).map(([id, field, label]) => (
          <div key={field} className="flex flex-col gap-1.5">
            <Label htmlFor={id}>{label}</Label>
            <Input
              id={id}
              // `inputMode="decimal"` rather than `type="number"`: a number input silently
              // permits scientific notation and, in some browsers, scrolls the value while
              // the pointer is over it. Neither is acceptable on a field that sets money.
              inputMode="decimal"
              placeholder="0.00"
              value={rates[field]}
              onChange={(event) =>
                setRates((current) => ({
                  ...current,
                  [field]: event.target.value,
                }))
              }
              disabled={mutation.isPending}
              className="font-mono"
            />
          </div>
        ))}
      </fieldset>

      {mutation.isError ? (
        <p role="alert" className="text-destructive text-sm">
          {mutation.error.message}
        </p>
      ) : null}

      <div className="flex items-center gap-2">
        <Button
          type="submit"
          size="sm"
          disabled={!complete || mutation.isPending}
        >
          {mutation.isPending ? 'Recording…' : 'Record rate'}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onDone}
          disabled={mutation.isPending}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}

/**
 * Where "Effective from" starts. The start of the current UTC month is the answer
 * nearly always wanted — the gap being fixed is in the period on screen — and it stays
 * editable because a rate that began earlier is how a *past* month gets corrected.
 *
 * Unless the current rate began this month. The 1st would then sit before it, so the
 * new rate would cover the days up to the current one's start and lose to it from then
 * on — a "Change rate" that silently changed almost nothing. Today is used instead: a
 * rate that began today is corrected in place (the backend overwrites same-day
 * collisions), and one that began earlier this month is superseded from today, the
 * earliest date the backend still accepts for it.
 */
function defaultEffectiveFrom(
  currentPrice: ModelPriceView | undefined,
): string {
  const now = new Date();
  const monthStart = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}-01`;
  // Through `Date`, not a slice of the string: a timestamp with an offset carries its
  // local date, which can be a day off the UTC one the month boundary is in.
  const currentFrom =
    currentPrice &&
    new Date(currentPrice.effective_from).toISOString().slice(0, 10);
  if (!currentFrom || currentFrom < monthStart) return monthStart;
  return now.toISOString().slice(0, 10);
}

/** "15 Oct", with the year when not this one — the product's one short date format. */
function shortUtcDate(at: string): string {
  const date = new Date(at);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
    ...(date.getUTCFullYear() === new Date().getUTCFullYear()
      ? {}
      : { year: 'numeric' }),
  });
}
